import SidebarAccordionAPI from '@/features/menu-management/SidebarAccordion/api/SidebarAccordionAPI'
import { QUERY as SIDEBAR_ACCORDION_QUERY } from '@/features/menu-management/SidebarAccordion/api/SidebarAccordionAPI.model'
import { useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { QUERY as DISHES_QUERY } from '@/features/menu-management/Dishes/api/DishesAPI.model'
import { QUERY as TABLE_CARDS_MANAGEMENT_QUERY } from '@/features/table-cards/api/TableCardsManagementAPI.model'
import TableCardsManagementAPI from '@/features/table-cards/api/TableCardsManagementAPI'
import DishesAPI from '@/features/menu-management/Dishes/api/DishesAPI'
import MobileAppManagementAPI from '@/features/mobile-app/api/MobileAppManagementAPI'
import { QUERY as MOBILE_APP_MANAGEMENT_QUERY } from '@/features/mobile-app/api/MobileAppManagementAPI.model'
import IngredientsAPI from '@/features/menu-management/Ingredients/api/IngredientsAPI'
import { QUERY as INGREDIENTS_QUERY } from '@/features/menu-management/Ingredients/api/IngredientsAPI.model'
import ToppingsAPI from '@/features/menu-management/Toppings/api/ToppingsAPI'
import { QUERY as TOPPINGS_QUERY } from '@/features/menu-management/Toppings/api/ToppingsAPI.model'
import ScheduleAPI from '@/features/employee-schedule/api/ScheduleAPI'
import { SCHEDULE_QUERY } from '@/features/employee-schedule/api/ScheduleAPI.model'
import { SCHEDULE_USE_API } from '@/features/employee-schedule/config'
import {
  startOfWeek,
  toDateKey
} from '@/features/employee-schedule/utils/schedule'

const usePreloadQueries = () => {
  const { i18n } = useTranslation()
  const currentLanguage = localStorage.getItem('i18nextLng') || i18n.language

  /*
    The week the roster opens on. Computed the same way the schedule page does,
    because the point is to warm THAT cache entry — a key that differs by a day
    would fetch a week nobody is about to look at and leave the page cold.
  */
  const currentWeekStart = toDateKey(startOfWeek(dayjs()))

  const queryResults = useQueries({
    queries: [
      {
        queryKey: [SIDEBAR_ACCORDION_QUERY.LANGUAGES],
        queryFn: SidebarAccordionAPI.getLanguageList
      },
      {
        queryKey: [SIDEBAR_ACCORDION_QUERY.MENU_TYPES],
        queryFn: SidebarAccordionAPI.getMenuTypes
      },
      {
        queryKey: [SIDEBAR_ACCORDION_QUERY.DISH_CATEGORIES],
        queryFn: SidebarAccordionAPI.getDishCategories
      },
      {
        queryKey: [SIDEBAR_ACCORDION_QUERY.DISH_SIZES],
        queryFn: SidebarAccordionAPI.getDishSizes
      },
      {
        queryKey: [DISHES_QUERY.DISHES],
        queryFn: DishesAPI.getDishes
      },
      {
        queryKey: [DISHES_QUERY.SPICE_LEVELS],
        queryFn: DishesAPI.getAllSpiceLevels
      },
      {
        queryKey: [DISHES_QUERY.DIETS],
        queryFn: DishesAPI.getAllDiets
      },
      {
        queryKey: [TABLE_CARDS_MANAGEMENT_QUERY.QR_CODE_CARDS],
        queryFn: TableCardsManagementAPI.getQrCodeCards
      },
      {
        queryKey: [TABLE_CARDS_MANAGEMENT_QUERY.TABLES],
        queryFn: TableCardsManagementAPI.getTables
      },
      {
        queryKey: [MOBILE_APP_MANAGEMENT_QUERY.MOBILE_APP_DATA],
        queryFn: MobileAppManagementAPI.getMobileAppData
      },
      {
        queryKey: [INGREDIENTS_QUERY.INGREDIENTS],
        queryFn: IngredientsAPI.getIngredients
      },
      {
        queryKey: [INGREDIENTS_QUERY.ALLERGENS],
        queryFn: IngredientsAPI.getAllergens
      },
      {
        queryKey: [INGREDIENTS_QUERY.UNITS],
        queryFn: IngredientsAPI.getUnits
      },
      {
        queryKey: [TOPPINGS_QUERY.TOPPINGS],
        queryFn: ToppingsAPI.getToppings
      },
      /*
        The roster's current week, so opening the schedule finds it already in
        the cache and renders the real week instead of its skeleton.

        `retry: false` unlike the rest: `/shifts` is guarded by `read:shifts`,
        a permission plenty of accounts do not have, and this array gates the
        app's first loading screen. With the default three retries and their
        backoff, a venue without the permission would sit on the splash for
        several seconds to learn nothing. Settling immediately costs that case
        nothing, and a transient failure is picked up by the schedule page's
        own query when it mounts.
      */
      {
        queryKey: [
          SCHEDULE_QUERY.WEEK,
          localStorage.getItem('restaurantId'),
          currentWeekStart
        ],
        queryFn: () => ScheduleAPI.getWeek(currentWeekStart),
        enabled: SCHEDULE_USE_API,
        retry: false
      }
      // {
      //   queryKey: ['insights', restaurantId, 'total-sales-value-general', start_date, end_date],
      //   queryFn: () =>
      //     insightsApi.useQuery('get', '/api/restaurants/{restaurantId}/total-sales-value-general', {
      //       params: { path: { restaurantId }, query: { start_date, end_date } }
      //     }),
      //   staleTime,
      //   refetchOnWindowFocus: false,
      //   refetchOnMount: false,
      //   refetchOnReconnect: false,
      //   enabled: !!restaurantId
      // },
      // {
      //   queryKey: ['insights', restaurantId, 'total-sales-count-general', start_date, end_date],
      //   queryFn: () =>
      //     insightsApi.useQuery('get', '/api/restaurants/{restaurantId}/total-sales-count-general', {
      //       params: { path: { restaurantId }, query: { start_date, end_date } }
      //     }),
      //   staleTime,
      //   refetchOnWindowFocus: false,
      //   refetchOnMount: false,
      //   refetchOnReconnect: false,
      //   enabled: !!restaurantId
      // },
      // {
      //   queryKey: ['insights', restaurantId, 'total-sales-value-averages-general', start_date, end_date],
      //   queryFn: () =>
      //     insightsApi.useQuery('get', '/api/restaurants/{restaurantId}/total-sales-value-averages-general', {
      //       params: { path: { restaurantId }, query: { start_date, end_date } }
      //     }),
      //   staleTime,
      //   refetchOnWindowFocus: false,
      //   refetchOnMount: false,
      //   refetchOnReconnect: false,
      //   enabled: !!restaurantId
      // },
      // {
      //   queryKey: ['insights', restaurantId, 'top-by-units-toppings', start_date, end_date, 10],
      //   queryFn: () =>
      //     insightsApi.useQuery('get', '/api/restaurants/{restaurantId}/top-by-units-toppings', {
      //       params: { path: { restaurantId }, query: { start_date, end_date, limit: 10 } }
      //     }),
      //   staleTime,
      //   refetchOnWindowFocus: false,
      //   refetchOnMount: false,
      //   refetchOnReconnect: false,
      //   enabled: !!restaurantId
      // },
      // {
      //   queryKey: ['insights', restaurantId, 'top-by-units-dishes', start_date, end_date, 10],
      //   queryFn: () =>
      //     insightsApi.useQuery('get', '/api/restaurants/{restaurantId}/top-by-units-dishes', {
      //       params: { path: { restaurantId }, query: { start_date, end_date, limit: 10 } }
      //     }),
      //   staleTime,
      //   refetchOnWindowFocus: false,
      //   refetchOnMount: false,
      //   refetchOnReconnect: false,
      //   enabled: !!restaurantId
      // },
      // {
      //   queryKey: ['insights', restaurantId, 'top-by-units-ingredients-mv', start_date, end_date, 10, ingredient_type_filter],
      //   queryFn: () =>
      //     insightsApi.useQuery('get', '/api/restaurants/{restaurantId}/top-by-units-ingredients-mv', {
      //       params: {
      //         path: { restaurantId },
      //         query: { start_date, end_date, limit: 10, type_filter: ingredient_type_filter }
      //       }
      //     }),
      //   staleTime,
      //   refetchOnWindowFocus: false,
      //   refetchOnMount: false,
      //   refetchOnReconnect: false,
      //   enabled: !!restaurantId && !!ingredient_type_filter // or allow without filter if backend supports it
      // }
    ]
  })

  // Keyed by the interface language, so a language change loads it again.
  const selectedLanguages = useQuery({
    queryKey: [SIDEBAR_ACCORDION_QUERY.SELECTED_LANGUAGES, currentLanguage],
    queryFn: () => SidebarAccordionAPI.getSelectedLangauges()
  })

  /*
    The splash covers the first load and a language change, nothing else.

    It used to follow every query here for as long as the app was open. A
    query with no data goes back to loading whenever it fetches again, and
    react-query does fetch it again: when a page mounts its own observer of
    the same key (refetchOnMount: false does not stop that for a query
    without data), when a save removes it from the cache, when the week
    rolls over. One failed query, such as the roster for an account without
    `read:shifts`, was then enough to swap the whole app for the splash on
    opening a tab, unmounting the page that asked. Once everything has
    settled once, a later fetch is the page's to show, in its own skeleton.
  */
  const [preloaded, setPreloaded] = useState(false)
  const settled =
    !selectedLanguages.isLoading &&
    queryResults.every(query => !query.isLoading)
  // Set during render rather than in an effect, so the frame that settles
  // already shows the app instead of one more frame of splash.
  if (settled && !preloaded) setPreloaded(true)

  return {
    isLoading: !(preloaded || settled) || selectedLanguages.isLoading
  }
}

export default usePreloadQueries
